import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LookalikeFormSegment } from '../../../../../types.ts';
import { useReferenceData } from '../../../hooks/useReferenceData.ts';
import { BasicInfo } from '../../drawer/components/SegmentListItem/BasicInfo.tsx';
import { SegmentHeader } from '../../drawer/components/SegmentListItem/SegmentHeader.tsx';
import type { LookalikeEditableElements } from '../../drawer/components/SegmentListItem/useSegmentHeaderElements.tsx';
import { createSegmentMetadata } from '../../drawer/components/SegmentsList/utils/createSegmentMetadata.ts';
import { formatReachValue } from '../../drawer/components/SegmentsList/utils/formatReachValue.ts';
import { createBasicInfoPropsFromLookalike } from '../../utils/createBasicInfoProps.ts';
import { headerRowStyles } from '../../utils/segmentListItemStyles.ts';

export type SimilaritySegmentItemProps = {
  segment: LookalikeFormSegment;
  headerConfig?: LookalikeEditableElements;
  isSelectedItem?: boolean;
};

export const SimilaritySegmentItem = ({
  segment,
  headerConfig,
  isSelectedItem = false,
}: SimilaritySegmentItemProps) => {
  const { t } = useTranslation();
  const { organizations, providers } = useReferenceData();

  const basicInfoProps = useMemo(() => {
    const primaryMetadata = createSegmentMetadata(segment, t, segment.account_id || '');
    const displayReach = formatReachValue(t, primaryMetadata.reach);
    const organizationName = segment.account_id ? organizations[segment.account_id] : t('N / A');
    const providerName = segment.provider_id ? providers[segment.provider_id] : null;

    const basicInfoProps = createBasicInfoPropsFromLookalike({
      id: primaryMetadata.id,
      reach: primaryMetadata.reach,
      displayReach,
      category: primaryMetadata.category || '',
      organizationName,
      providerName: providerName || undefined,
      isSelectedList: true,
      lookalikeReachRange: primaryMetadata.lookalikeReachRange,
      isSeed: false,
    });

    return basicInfoProps;
  }, [segment, t, organizations, providers]);

  const headerContent = useMemo(() => {
    return headerConfig ? (
      <SegmentHeader
        name={segment.name}
        isLAL={true}
        config={headerConfig}
        isSelectedItem={isSelectedItem}
        isSeedAggregate={false}
      />
    ) : (
      <Box sx={headerRowStyles}>
        <Typography
          variant="body1Bold"
          sx={{
            flexGrow: 1,
          }}
        >
          {segment.name}
        </Typography>
      </Box>
    );
  }, [headerConfig, isSelectedItem, segment.name]);

  return (
    <>
      {headerContent}
      <BasicInfo {...basicInfoProps} />
    </>
  );
};
